// Helpers for the `com.nurion.channel_type` room state event.
//
// Three values, defaulting to `mixed`:
//   - human-only: humans only; agents must not post.
//   - mixed:      humans and agents (default for new rooms).
//   - agent-only: agents only; for inter-agent coordination channels.
//
// The state event has an empty state key. Setting it requires admin power
// level — this helper just sends; the SDK surfaces permission errors.

import type { Client } from "~/lib/matrix";

export const CHANNEL_TYPE_EVENT = "com.nurion.channel_type" as const;
export const CHANNEL_TYPES = ["human-only", "mixed", "agent-only"] as const;
export type ChannelType = (typeof CHANNEL_TYPES)[number];
export const DEFAULT_CHANNEL_TYPE: ChannelType = "mixed";

interface ChannelTypeContent {
  type: ChannelType;
}

function isChannelType(value: unknown): value is ChannelType {
  return typeof value === "string" && (CHANNEL_TYPES as readonly string[]).includes(value);
}

export async function getChannelType(client: Client, roomId: string): Promise<ChannelType> {
  const content = await client.bindings.getRoomStateEvent<ChannelTypeContent>(
    roomId,
    CHANNEL_TYPE_EVENT,
  );
  if (!content || !isChannelType(content.type)) {
    return DEFAULT_CHANNEL_TYPE;
  }
  return content.type;
}

export async function getChannelTypeState(
  client: Client,
  roomId: string,
): Promise<ChannelType | null> {
  const content = await client.bindings.getRoomStateEvent<ChannelTypeContent>(
    roomId,
    CHANNEL_TYPE_EVENT,
  );
  if (!content || !isChannelType(content.type)) return null;
  return content.type;
}

export async function setChannelType(
  client: Client,
  roomId: string,
  type: ChannelType,
): Promise<void> {
  const content: ChannelTypeContent = { type };
  await client.bindings.sendRoomStateEvent(roomId, CHANNEL_TYPE_EVENT, content);
}
